import React, { Fragment, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import {
  Button,
  InputField,
  ItemsPerPage,
  Loading,
  Modal,
  Pagination,
  SectionTitle,
  SelectField,
  Table,
  TableInput,
} from '../../../../components';

import styles from './index.module.css';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { faTrashAlt } from '@fortawesome/free-regular-svg-icons';

import DefaultSoil from './DefaultSoil';
import DefaultCptSoilService from '../../../../services/CptPile/DefaultSoil';

/**
 * Modal edycji profilu CPT (układ jak client/src/pages/CptPile/model/SoilTable)
 *
 * Oczekiwany model wiersza (backend soilsCpt.validation):
 *  - layerIndex, name, z, y, qc, cu, soilType, layerNo, zSoil, cd
 */
const CptSoilTableModal = ({
  open,
  title,

  onHide,
  onChange,
  onBlur,
  profileName = {},

  soilTable,
  onChangeSoilState,
  onBlurSoilState,
  onKeyDown,

  removeRow,
  addRow,
  addRowStart,

  // NEW: potrzebne aby DefaultSoil mógł wypełniać wiersz w CPT tabeli
  updateCptSoilState,

  saveButtonTitle,
  loading,
  onSubmit,

  hideProfileName,
}) => {
  // DefaultSoil (skopiowany z CptPile) + service (CptPile/DefaultSoil)
  const { defaultSoilLayer } = DefaultCptSoilService(updateCptSoilState);

  const [openLayerModal, setOpenLayerModal] = useState(false);
  const [rowIndex, setRowIndex] = useState(null);

  const openDefaultSoilModal = (absoluteIndex) => {
    setRowIndex(absoluteIndex);
    setOpenLayerModal(true);
  };
  const hideDefaultSoilModal = () => setOpenLayerModal(false);

  // Pagination zgodna z components/Pagination
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [currentPage, setCurrentPage] = useState(1);

  const allItemsLength = Array.isArray(soilTable?.state) ? soilTable.state.length : 0;

  const countingItemsPerPage = useMemo(
    () => (currentPage - 1) * itemsPerPage,
    [currentPage, itemsPerPage],
  );

  const dataToPaginate = useMemo(() => {
    if (!Array.isArray(soilTable?.state)) return [];
    const start = countingItemsPerPage;
    const end = countingItemsPerPage + itemsPerPage;
    return soilTable.state.slice(start, end);
  }, [soilTable?.state, countingItemsPerPage, itemsPerPage]);

  const displayItemsNumber = (e) => {
    // ItemsPerPage odpala onClick na wrapperze i daje e.target.value
    const v = e?.target?.getAttribute?.('value') ?? e?.target?.value ?? e;
    const next = Number(v);
    if (!Number.isFinite(next) || next <= 0) return;
    setItemsPerPage(next);
    setCurrentPage(1);
  };

  const nextPage = (page) => {
    const p = Number(page);
    if (!Number.isFinite(p)) return;
    setCurrentPage(p);
  };

  return (
    <Fragment>
      <Modal
        open={open}
        onHide={onHide}
        css={styles.modal}
        cssDialog={styles.modalDialog}
        title={title}
        hideButton={false}
      >
        {!hideProfileName && (
          <Row className={styles.row}>
            <Col xs={12}>
              <SectionTitle title="Nazwa profilu CPT" />
              <InputField
                onChange={onChange}
                onBlur={onBlur}
                value={profileName.value}
                isValid={profileName.isValid}
                type="text"
                title=""
                placeholder="Nazwa profilu"
                name="profileName"
                required
              />
            </Col>
          </Row>
        )}

        <Row className={styles.row}>
          <Col xs={12}>
            <div className={styles.titleAndActions}>
              <SectionTitle title="Parametry podłoża gruntowego" />

            </div>

            <ItemsPerPage
              onDisplayItemsNumber={displayItemsNumber}
              itemsPerPage={itemsPerPage}
              totalItems={allItemsLength}
              display={[15, 30, allItemsLength]}
            />

            <Table>
              <thead className={styles.thead}>
                <tr>
                  <th>
                    L.p.<br />[-]
                  </th>
                  <th title="Numer/nazwa warstwy">
                    Nazwa warstwy<br />[-]
                  </th>
                  <th title="Zagłębienie warstwy">
                    Z<br />[m.p.p.t]
                  </th>
                  <th title="Ciężar objętościowy gruntu">
                    &#947; <br />[kN/m<sup>3</sup>]
                  </th>
                  <th title="Opór na stożku">
                    q<sub>c</sub> <br />[MPa]
                  </th>
                  <th title="Wytrzymałość gruntu na ścinanie bez odpływu">
                    c<sub>u</sub> <br />[kPa]
                  </th>
                  <th width={130}>
                    Rodzaj gruntu <br />[-]
                  </th>
                  <th>Usuń</th>
                  <th>Dodaj</th>
                </tr>
              </thead>

              <tbody className={styles.tbody}>
                {dataToPaginate.map((element, index) => {
                  const absoluteIndex = countingItemsPerPage + index;

                  const layerIndex = element?.layerIndex?.value ?? absoluteIndex + 1;

                  const soilTypeValueRaw = element?.soilType?.value;
                  const soilTypeDisplay =
                    soilTypeValueRaw === 0
                      ? 'Niespoisty'
                      : soilTypeValueRaw === 1
                      ? 'Spoisty'
                      : soilTypeValueRaw || '';

                  return (
                    <tr
                      key={absoluteIndex}
                      className={Number(layerIndex) % 2 === 0 ? 'shadowTr' : ''}
                    >
                      <td>{absoluteIndex + 1}</td>

                      <td>
                        <TableInput
                          name="name"
                          placeholder="Nazwa warstwy"
                          type="string"
                          align="center"
                          data-row={absoluteIndex}
                          onBlur={onBlurSoilState}
                          onChange={onChangeSoilState}
                          onKeyDown={onKeyDown}
                          value={element?.name?.value ?? ''}
                          isValid={element?.name?.isValid ?? null}
                          required
                        />
                      </td>

                      <td>
                        <TableInput
                          name="z"
                          type="number"
                          min="0"
                          align="center"
                          data-row={absoluteIndex}
                          onBlur={onBlurSoilState}
                          onChange={onChangeSoilState}
                          onKeyDown={onKeyDown}
                          value={element?.z?.value ?? ''}
                          isValid={element?.z?.isValid ?? null}
                          required
                        />
                      </td>

                      <td>
                        <TableInput
                          name="y"
                          type="number"
                          min="0"
                          align="center"
                          data-row={absoluteIndex}
                          onBlur={onBlurSoilState}
                          onChange={onChangeSoilState}
                          onKeyDown={onKeyDown}
                          value={element?.y?.value ?? ''}
                          isValid={element?.y?.isValid ?? null}
                          required
                        />
                      </td>

                      <td>
                        <TableInput
                          name="qc"
                          type="number"
                          min="0"
                          align="center"
                          data-row={absoluteIndex}
                          onChange={onChangeSoilState}
                          onBlur={onBlurSoilState}
                          onKeyDown={onKeyDown}
                          value={element?.qc?.value ?? ''}
                          isValid={element?.qc?.isValid ?? null}
                          required
                        />
                      </td>

                      <td>
                        <TableInput
                          name="cu"
                          type="number"
                          min="0"
                          align="center"
                          data-row={absoluteIndex}
                          onChange={onChangeSoilState}
                          onBlur={onBlurSoilState}
                          onKeyDown={onKeyDown}
                          value={element?.cu?.value ?? ''}
                          isValid={element?.cu?.isValid ?? null}
                          required
                        />
                      </td>

                      <td>
                        <SelectField
                          name="soilType"
                          title=""
                          data-row={absoluteIndex}
                          onChange={onChangeSoilState}
                          value={soilTypeDisplay}
                          defaultOption="Typ gruntu"
                          options={['Niespoisty', 'Spoisty']}
                          required
                        />
                      </td>

                      <td>
                        <Button
                          variant="danger"
                          icon={faTrashAlt}
                          altTitle={'Usuń'}
                          onClick={() => removeRow(absoluteIndex)}
                          disabled={allItemsLength <= 1}
                        />
                      </td>

                      <td>
                        {/* zielony plus – otwiera DefaultSoil dla tego wiersza */}
                        <Button
                          variant="info"
                          icon={faPlus}
                          altTitle={'Dodaj (domyślne parametry)'}
                          onClick={() => openDefaultSoilModal(absoluteIndex)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            <div className={styles.addButtons}>
                <Button
                  variant="success"
                  icon={faPlus}
                  css="default-button-style"
                  title="Dodaj warstwę na początku"
                  onClick={addRowStart}
                />
                <Button
                  variant="success"
                  icon={faPlus}
                  css="default-button-style"
                  title="Dodaj warstwę na końcu"
                  onClick={addRow}
                />
              </div>

            {allItemsLength > itemsPerPage && (
              <Pagination
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                totalItems={allItemsLength}
                nextPage={nextPage}
              />
            )}

            <div className={styles.footer}>
              <Button
                variant="success"
                css={`${styles.button} default-button-style`}
                onClick={onSubmit}
              >
                {saveButtonTitle || 'Zapisz zmiany'}
              </Button>
            </div>

            {loading?.loading && <Loading />}

            {/* DefaultSoil (kopiowany z CptPile) */}
            <DefaultSoil
              {...defaultSoilLayer}
              open={openLayerModal}
              onHide={hideDefaultSoilModal}
              rowIndex={rowIndex}
            />
          </Col>
        </Row>
      </Modal>
    </Fragment>
  );
};

export default CptSoilTableModal;
